/**
 * Authentication API Client
 *
 * Axios-based API client for authentication endpoints:
 * - POST /v1/auth/login
 * - POST /v1/auth/logout
 * - POST /v1/auth/refresh
 *
 * @see specs/002-frontend-mvp/contracts/api-specification.md - Authentication Endpoints
 */

import { api } from '../../../lib/axios';
import type { User } from '../../../types/user';

/**
 * Login Request
 */
export interface LoginRequest {
  email: string;
  apiKey: string;
}

/**
 * Login Response
 */
export interface LoginResponse {
  token: string;
  refreshToken: string;
  expiresAt: string;
  user: User;
}

/**
 * Refresh Token Request
 */
export interface RefreshTokenRequest {
  refreshToken: string;
}

/**
 * Refresh Token Response
 */
export interface RefreshTokenResponse {
  token: string;
  expiresAt: string;
}

/**
 * Backend API Login Response (snake_case format)
 */
interface BackendLoginResponse {
  success: boolean;
  user: {
    user_id: string;
    email: string;
  };
  session: {
    session_id: string;
    agent_name: string;
  };
  tokens: {
    access_token: string;
    refresh_token: string;
    expires_in: number;
    token_type: string;
  };
}

/**
 * Login with email and API key
 *
 * @param credentials - Email and API key
 * @returns Session tokens and user data
 * @throws {AxiosError} 401 - Invalid credentials, 400 - Validation error
 */
export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  // Convert frontend field names to backend format (apiKey -> api_key)
  const backendCredentials = {
    email: credentials.email,
    api_key: credentials.apiKey,
  };
  const response = await api.post<BackendLoginResponse>('/v1/auth/login', backendCredentials);

  // Transform backend response to frontend format
  const { user, tokens } = response.data;
  const expiresAt = new Date(Date.now() + tokens.expires_in * 1000).toISOString();

  return {
    token: tokens.access_token,
    refreshToken: tokens.refresh_token,
    expiresAt,
    user: {
      id: user.user_id,
      email: user.email,
      createdAt: new Date().toISOString(), // Backend doesn't return this, use current time
      preferences: {
        theme: 'system' as const, // Default theme preference
      },
    },
  };
}

/**
 * Logout and revoke current session
 *
 * @throws {AxiosError} 401 - Invalid or missing token
 */
export async function logout(): Promise<void> {
  await api.post('/v1/auth/logout');
}

/**
 * Refresh access token using refresh token
 *
 * @param request - Refresh token
 * @returns New access token and expiration
 * @throws {AxiosError} 401 - Invalid or expired refresh token
 */
export async function refreshToken(request: RefreshTokenRequest): Promise<RefreshTokenResponse> {
  // Convert frontend field names to backend format (refreshToken -> refresh_token)
  const backendRequest = {
    refresh_token: request.refreshToken,
  };
  const response = await api.post<RefreshTokenResponse>('/v1/auth/refresh', backendRequest);
  return response.data;
}
